import { useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link, TableCell, TableContainer } from '@mui/material';
import { defaultComparator } from '@lib/utils';
import MuiTable from '@components/common/MuiTable';
import DeliveryLocationCell from '@components/ContentComponents/Sessions/DeliveryLocationCell';
import { formatSessionDates, getDeliveryInfo, getSessionName, getSessionNumbers, } from './sessionFunctions';
import { getCurriculumName, getStatusIcon } from '../eventFunctions';
import StatusLegend from './StatusLegend';

/**
 * Returns a comparison function.
 *
 * @param order
 * @param orderBy
 */
function getComparator(order, orderBy) {
  const ord = order === 'desc' ? -1 : 1;
  switch (orderBy) {
    case 'start':
      return (a, b) => {
        if (a === b) return 0;
        if (!a?.mStart) return -1 * ord;
        if (!b?.mStart) return 1 * ord;
        return a.mStart.diff(b.mStart) * ord;
      };
    case 'curriculum':
      const getCurriculumName = (session) => session?.event?.curriculum?.name || 'Booth';
      return (a, b) => getCurriculumName(a).localeCompare(getCurriculumName(b)) * ord;
    case 'delivery_site':
      return (a, b) => {
        const aStrings = getDeliveryInfo(a);
        const bStrings = getDeliveryInfo(b);
        return aStrings.join('').localeCompare(bStrings.join('')) * ord;
      }
    case 'session_name':
      return (a, b) => {
        return getSessionName({ session: a }).localeCompare(getSessionName({ session: b })) * ord;
      };
    case 'created_by':
      // Note: This column shows the event's creator, not the session's creator.
      return (a, b) => {
        const aName = a?.event.created_by_names || a?.event.created_by;
        const bName = b?.event.created_by_names || b?.event.created_by;
        return aName.localeCompare(bName) * ord;
      };
    case 'status':
      const getStatusOrd = (status) => {
        switch (status) {
          case 'COMPLETED':
            return 2;
          case 'CANCELED':
            return 0;
          case 'INCOMPLETE':
          default:
            return 1;
        }
      };
      return (a, b) => (getStatusOrd(a.status) - getStatusOrd(b.status)) * ord;
    default:
      return defaultComparator;
  }
}

const SessionsTable = (props) => {
  const { sessions, columns, legend = false, defaultOrderDir  } = props;

  const headCells = [
    {
      id: 'start',
      label: 'Date & Time',
    },
    {
      id: 'curriculum',
      label: 'Curriculum/Booth',
    },
    {
      id: 'session_name',
      label: 'Session Name',
    },
    {
      id: 'delivery_site',
      label: 'Delivery Site',
    },
    {
      id: 'created_by',
      label: 'Created By',
    },
    {
      id: 'status',
      label: 'Status',
    }
  ].filter(({ id }) => columns.includes(id));

  const getRowColumns = useCallback((row) => columns.reduce((acc, column) => {
    switch (column) {
      case 'start':
        acc.push(
          <TableCell align="left" sx={{ minWidth: '210px' }} data-cy="date-cell">
            <Link to={`/events/${row.event_id}/${row.id}/`} component={RouterLink}>
              { formatSessionDates(row.start, row.end) }
            </Link>
          </TableCell>
        );
        break;
      case 'curriculum':
        acc.push(
          <TableCell component="th" scope="row" data-cy="status-cell">
            { getCurriculumName(row?.event) }
          </TableCell>
        );
        break;
      case 'session_name':
        acc.push(
          <TableCell component="th" scope="row" data-cy="name-cell">
            { getSessionNumbers(row) } { getSessionName({ session: row }) }
          </TableCell>
        );
        break;
      case 'delivery_site':
        acc.push(<DeliveryLocationCell session={row} />);
        break;
      case 'created_by':
        acc.push(
          <TableCell align="left" data-cy="owner-cell">{row.event.created_by_names}</TableCell>
        );
        break;
      case 'status':
        acc.push(
          <TableCell component="th" scope="row" data-cy="status-cell">
            { getStatusIcon(row.status) }
          </TableCell>
        );
        break;
      default: break;
    }
    return acc;
  }, []), [formatSessionDates, getSessionName, getStatusIcon]);

  return (
    <>
      <TableContainer>
        {legend &&
          <StatusLegend />
        }
        <MuiTable
          defaultOrderBy="start"
          defaultOrderDir={defaultOrderDir || 'asc'}
          headCells={headCells}
          getComparator={getComparator}
          data={sessions}
          alignTop
        >
          {(row) => getRowColumns(row)}
        </MuiTable>
      </TableContainer>
    </>
  );
};

export default SessionsTable;
